import { useState } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { getSourcexTemplatesPath, getSourcexPatientsPath } from "../services/firestorePaths";
import { v4 as uuidv4 } from "uuid";
import { motion } from "framer-motion";
import { UserPlus, X, Loader2, AlertTriangle } from "lucide-react";
import { useDialog } from "../components/sourcex/useDialog";
import {
  normalizeTemplate,
  stripAnswers,
  countQuestions,
} from "../services/templateSchema";

const FIELDS = [
  { key: "name", label: "Patient Name", type: "text", required: true },
  { key: "dob", label: "Date of Birth", type: "date" },
  { key: "randomization", label: "Randomization #", type: "text" },
  { key: "screening", label: "Screening #", type: "text" },
];

const inputCls =
  "w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition [color-scheme:dark]";

export default function AddPatientModal({ studyId, onClose, onPatientAdded }) {
  const [form, setForm] = useState({
    name: "",
    dob: "",
    randomization: "",
    screening: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const { confirm: askConfirm, dialog } = useDialog();

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setError("");
    if (!studyId) {
      setError("Missing study. Please select a study before adding a patient.");
      return;
    }
    if (!form.name.trim()) {
      setError("Patient name is required.");
      return;
    }

    setSaving(true);
    try {
      const id = uuidv4();

      // ✅ Get study template (snapshot it onto the patient)
      const templateRef = doc(db, getSourcexTemplatesPath(), studyId);
      const templateSnap = await getDoc(templateRef);
      if (!templateSnap.exists()) {
        setError("This study has no source template yet. Build one before enrolling patients.");
        setSaving(false);
        return;
      }

      // Normalize first so the snapshot carries stable question ids, then strip
      // answers to guarantee a blank form. stripAnswers is type-aware — a
      // checklist starts as [], a table as [], a checkbox as false. The old
      // hardcoded `value: ""` produced a string where those types expect a
      // container, which renders as an empty field that silently drops input.
      const template = normalizeTemplate(templateSnap.data());

      // A draft is explicitly "not active until approved" (see SectionEditor's
      // own banner), and a source document is the regulated record a monitor
      // reads. Enrolling against a draft is therefore called out explicitly —
      // but not blocked, so SourceX stays testable before templates are signed
      // off. `templateStatusAtSnapshot` below records which it actually was.
      if (template.status !== "approved") {
        const proceed = await askConfirm(
          "This study's source template is still a DRAFT — it has not been reviewed and approved.\n\n" +
            "This patient's source document will be built from an unapproved template, and later template changes will not flow into it.",
          {
            title: "Enroll against an unapproved template?",
            confirmLabel: "Enroll anyway",
            tone: "danger",
          }
        );
        if (!proceed) {
          setSaving(false);
          return;
        }
      }

      if (countQuestions(template) === 0) {
        setError("This study's template has no questions — nothing to record against.");
        setSaving(false);
        return;
      }

      const newPatient = {
        ...form,
        name: form.name.trim(),
        studyId,
        id,
        status: "Enrolled",
        createdAt: new Date().toISOString(),
        // Provenance: which template version this patient's source doc was cut
        // from, so a later template change is traceable rather than invisible.
        templateSchemaVersion: template.schemaVersion,
        templateSnapshotAt: new Date().toISOString(),
        // Whether the template was approved AT THE MOMENT this source document
        // was cut. Approving the template later does not retroactively make
        // this patient's doc approved-derived, so it must be recorded here
        // rather than inferred from the template's current status.
        templateStatusAtSnapshot: template.status || "draft",
        data: stripAnswers(template).sections,
      };

      await setDoc(doc(db, getSourcexPatientsPath(), id), newPatient);
      onPatientAdded?.(); // refresh list
      onClose();
    } catch (err) {
      console.error("❌ Error adding patient:", err);
      setError("Failed to add patient. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4 font-[Manrope]">
      {/* Sits above this modal (z-[100]) so the confirmation is centered on
          screen rather than pinned to the top by the browser. */}
      {dialog}
      <motion.div
        initial={{ opacity: 0, y: 14, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ type: "spring", stiffness: 280, damping: 24 }}
        className="w-full max-w-md rounded-2xl border border-white/10 bg-[#0b0820]/95 backdrop-blur-2xl shadow-2xl shadow-black/50 overflow-hidden text-slate-100"
      >
        {/* Header */}
        <div className="relative px-5 py-4 border-b border-white/10 bg-gradient-to-r from-purple-600/80 to-blue-600/80">
          <button
            onClick={onClose}
            className="absolute right-3 top-3 w-7 h-7 rounded-lg flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-base font-semibold flex items-center gap-2">
            <UserPlus className="w-4 h-4" /> Add Patient
          </h2>
        </div>

        <div className="p-5">
          {error && (
            <div className="mb-3 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-3">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-xs uppercase tracking-wider font-medium text-slate-400 mb-1">
                  {f.label}
                  {f.required && <span className="text-purple-300"> *</span>}
                </label>
                <input
                  type={f.type}
                  name={f.key}
                  value={form[f.key]}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder={f.type === "text" ? f.label : undefined}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-white/10 bg-white/[0.02] flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/[0.06] transition"
          >
            Cancel
          </button>
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" /> Save Patient
              </>
            )}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
}
